/* ==========================================================================
   Sound Event Detector - Main Application Router & Entry Point
   ========================================================================== */

import './css/main.css';
import { getLocale, initVisitorLanguageDetection } from './locales/i18n.js';
import { renderHeader } from './components/Header.js';
import { renderFooter } from './components/Footer.js';
import { renderCookieBanner } from './components/CookieBanner.js';
import { renderOpenClawChatWidget } from './components/OpenClawChatWidget.js';
import { renderArticleModal, openArticleModal } from './components/ArticleModal.js';
import { getArticleBySlug, initArticleStore } from './services/articleStore.js';

if (typeof window !== 'undefined') {
  window.openArticleModal = openArticleModal;
}

// Page Views
import { renderHomePage } from './pages/Home.js';
import { renderSolutionsPage } from './pages/Solutions.js';
import { renderDetectionPage } from './pages/Detection.js';
import { renderMarketsPage } from './pages/Markets.js';
import { renderCooperationPage } from './pages/Cooperation.js';
import { renderAboutUsPage } from './pages/AboutUs.js';
import { renderCaseStudiesPage } from './pages/CaseStudies.js';
import { renderContactPage } from './pages/Contact.js';
import { renderCookiePolicyPage } from './pages/CookiePolicy.js';

// News & Product Updates Pages
import { renderNewsHubPage, renderLatestNewsPage, renderNewsPage } from './pages/News.js';
import { renderProductUpdatesPage } from './pages/ProductUpdates.js';

// Expanded Legal & Partner Pages
import { renderDistributorsMapPage } from './pages/DistributorsMap.js';
import { renderPrivacyPolicyPage } from './pages/PrivacyPolicy.js';
import { renderTermsConditionsPage } from './pages/TermsConditions.js';
import { renderCodeOfEthicsPage } from './pages/CodeOfEthics.js';
import { renderPolicyIMSPage } from './pages/PolicyIMS.js';
import { renderManualsAndDatasheetsPage } from './pages/ManualsAndDatasheets.js';

const app = document.getElementById('app');

/**
 * Dynamic SEO & OpenGraph Meta Updater for SPA Routes & Articles
 */
function updatePageSeoMetadata(route) {
  const lang = getLocale() || 'en';
  const clean = (route || 'home').replace(/^#\/?/, '').replace(/\/+$/, '');

  // Check if this is an article route
  let articleSlug = null;
  let articleCategory = 'news';

  if (clean.startsWith('news/') || clean.startsWith('latest-news/')) {
    articleSlug = clean.replace(/^(latest-)?news\//, '');
    articleCategory = 'news';
  } else if (clean.startsWith('case-studies/')) {
    articleSlug = clean.replace(/^case-studies\//, '');
    articleCategory = 'case-studies';
  } else if (clean.startsWith('product-updates/')) {
    articleSlug = clean.replace(/^product-updates\//, '');
    articleCategory = 'product-updates';
  } else if (getArticleBySlug(clean, lang)) {
    articleSlug = clean;
    const a = getArticleBySlug(clean, lang);
    const c = (a.category || 'latest-news').toLowerCase();
    articleCategory = c.includes('case') ? 'case-studies' : (c.includes('product') ? 'product-updates' : 'news');
  }

  if (articleSlug) {
    const art = getArticleBySlug(articleSlug, lang);
    if (art) {
      const artTitle = `${art.title} | Sound Event Detector`;
      const artDesc = (art.excerpt || 'Official Sound Event Detector field report and analysis.').slice(0, 220);
      const canonical = `https://www.jalud.eu/${articleCategory}/${articleSlug}`;
      
      document.title = artTitle;
      try {
        const descEl = document.querySelector('meta[name="description"]');
        if (descEl) descEl.setAttribute('content', artDesc);
        const ogTitleEl = document.querySelector('meta[property="og:title"]');
        if (ogTitleEl) ogTitleEl.setAttribute('content', artTitle);
        const ogDescEl = document.querySelector('meta[property="og:description"]');
        if (ogDescEl) ogDescEl.setAttribute('content', artDesc);
        const ogUrlEl = document.querySelector('meta[property="og:url"]');
        if (ogUrlEl) ogUrlEl.setAttribute('content', canonical);
        const canonicalEl = document.querySelector('link[rel="canonical"]');
        if (canonicalEl) canonicalEl.setAttribute('href', canonical);
        if (art.img) {
          const ogImgEl = document.querySelector('meta[property="og:image"]');
          if (ogImgEl) ogImgEl.setAttribute('content', art.img.startsWith('http') ? art.img : `https://www.jalud.eu${art.img.startsWith('/') ? '' : '/'}${art.img}`);
        }
      } catch (e) {}
      return;
    }
  }
  const seoData = {
    'home': {
      title: 'Sound Event Detector | Real-Time AI Acoustic Security & Threat Detection',
      desc: 'Sound Event Detector (SED) provides real-time AI audio security detection for gunshots, panic screams, glass breaks, drones, and sirens.'
    },
    'solutions': {
      title: 'Acoustic Security Architecture & AI Solutions | Sound Event Detector',
      desc: 'Explore SED modular edge hardware and smart camera ACAP/HEOP plugins with sub-500ms latency and 100% GDPR compliance.'
    },
    'detection': {
      title: 'Acoustic Threat Detection Portfolio | Sound Event Detector',
      desc: 'Real-time AI audio classifiers for gunshots, glass break, drones, screams, aggression, angle grinders, and sirens.'
    },
    'detection-gunshot': {
      title: 'Real-Time AI Gunshot Detection & PTZ Auto-Slew | Sound Event Detector',
      desc: 'Ballistic impulse and firearm acoustic detection under 400ms with automated PTZ camera triangulation.'
    },
    'detection-glass': {
      title: 'Acoustic Glass Break Detection & Burglary Prevention | Sound Event Detector',
      desc: 'Dual-frequency acoustic fracture detection for commercial storefronts, banking facilities, and residential perimeters.'
    },
    'detection-drone': {
      title: '360° Drone & UAV Acoustic Perimeter Protection | Sound Event Detector',
      desc: 'Harmonic rotor audio detection for unauthorized drones in correctional facilities, airports, and critical infrastructure.'
    },
    'detection-scream': {
      title: 'Panic Scream & Verbal Aggression AI Classification | Sound Event Detector',
      desc: 'Multi-class acoustic distinction for panic screams, hostile shouting, and vocal stress in hospitals, schools, and cities.'
    },
    'detection-graffiti': {
      title: 'Aerosol Spray & Graffiti Vandalism Detection | Sound Event Detector',
      desc: 'Early acoustic identification of aerosol can agitation and nozzle spraying to protect public transit and monuments.'
    },
    'detection-grinder': {
      title: 'Angle Grinder & Metal Cutting Acoustic Detection | Sound Event Detector',
      desc: 'High-RPM cutting disc and perimeter fence breach acoustic detection for industrial yards and storage facilities.'
    },
    'detection-siren': {
      title: 'Emergency Vehicle Siren Recognition & Traffic Priority | Sound Event Detector',
      desc: 'Acoustic sweep frequency recognition for police, ambulance, and fire rescue priority in smart city traffic corridors.'
    },
    'markets': {
      title: 'Industry Solutions & Vertical Applications | Sound Event Detector',
      desc: 'Acoustic AI deployments for smart cities, healthcare hospitals, university campuses, industrial sites, and commercial real estate.'
    },
    'market-public': {
      title: 'Smart City & Municipal Public Safety Acoustic AI | Sound Event Detector',
      desc: 'Instant alert dispatch to municipal police command centers with 360-degree acoustic camera slewing.'
    },
    'market-healthcare': {
      title: 'Hospital & Healthcare Emergency Acoustic Protection | Sound Event Detector',
      desc: 'Protect ER medical staff against aggressive behavior and panic situations without recording patient voice.'
    },
    'market-education': {
      title: 'Campus & School Safety Acoustic Security | Sound Event Detector',
      desc: 'Active threat detection for educational corridors, outdoor athletic fields, and university facilities.'
    },
    'market-industrial': {
      title: 'Critical Infrastructure & Industrial Site Acoustic Security | Sound Event Detector',
      desc: 'Perimeter fence breach, metal cutting, explosion, and drone early-warning for high-security facilities.'
    },
    'market-private': {
      title: 'Commercial Real Estate & Retail Loss Prevention | Sound Event Detector',
      desc: 'After-hours burglary and glass breakage early detection for shopping centers, logistics depots, and storefronts.'
    },
    'cooperation': {
      title: 'Partnership Programs & Global Integrator Network | Sound Event Detector',
      desc: 'Partner with JALUD Embedded as a certified security distributor, VMS system integrator, or OEM technology partner.'
    },
    'distributors-map': {
      title: 'Global Distributors & Certified Integrators Map | Sound Event Detector',
      desc: 'Interactive worldwide directory of authorized SED security distributors and certified installation partners.'
    },
    'news': {
      title: 'Latest News, TV Reports & Press Releases | Sound Event Detector',
      desc: 'Latest company updates, TV news coverage, security awards, and international deployment announcements.'
    },
    'case-studies': {
      title: 'Field Case Studies & Deployment Results | Sound Event Detector',
      desc: 'Documented field results from smart city deployments, university campus security, and prison perimeter defense.'
    },
    'product-updates': {
      title: 'Product, Firmware & Algorithm Updates | Sound Event Detector',
      desc: 'Latest firmware releases, neural network model improvements, and VMS integration driver updates.'
    },
    'about-us': {
      title: 'About JALUD Embedded | Leadership & Acoustic Engineering Team',
      desc: 'Learn about JALUD Embedded, our founding story, mission, and the engineering team behind Sound Event Detector.'
    },
    'manuals-and-datasheets': {
      title: 'Technical Manuals, Datasheets & Certifications | Sound Event Detector',
      desc: 'Official technical specifications, ONVIF drivers, CE/FCC declarations of conformity, and GDPR legal opinion studies.'
    },
    'contact': {
      title: 'Contact Sales & Request Live Acoustic Demo | Sound Event Detector',
      desc: 'Get in touch with JALUD Embedded sales and acoustic engineering team for customized security assessments and live demos.'
    },
    'privacy-policy': {
      title: 'Privacy Policy & GDPR Compliance | Sound Event Detector',
      desc: 'Official privacy statement confirming zero audio recording, non-invasive RAM processing, and 100% GDPR conformity.'
    }
  };

  const meta = seoData[route] || seoData['home'];
  document.title = meta.title;

  try {
    const descEl = document.querySelector('meta[name="description"]');
    if (descEl) descEl.setAttribute('content', meta.desc);

    const ogTitleEl = document.querySelector('meta[property="og:title"]');
    if (ogTitleEl) ogTitleEl.setAttribute('content', meta.title);

    const ogDescEl = document.querySelector('meta[property="og:description"]');
    if (ogDescEl) ogDescEl.setAttribute('content', meta.desc);

    const ogUrlEl = document.querySelector('meta[property="og:url"]');
    if (ogUrlEl) ogUrlEl.setAttribute('content', `https://www.jalud.eu/#${route === 'home' ? '' : route}`);

    const canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) canonicalEl.setAttribute('href', `https://www.jalud.eu/#${route === 'home' ? '' : route}`);
  } catch (e) {}
}

function getInitialRoute() {
  const path = window.location.pathname.replace(/^\/+/, '').replace(/\/+$/, '');
  const hash = window.location.hash.replace(/^#\/?/, '').replace(/\/+$/, '');

  const langCodes = ['en', 'cs', 'de', 'es', 'pt', 'ar'];
  if (hash) {
    if (langCodes.includes(hash.toLowerCase())) {
      return 'home';
    }
    return hash;
  }
  if (path && (path.startsWith('news/') || path.startsWith('latest-news/') || path.startsWith('case-studies/') || path.startsWith('product-updates/'))) {
    return path;
  }
  return path || 'home';
}

let lastTrackedPath = null;

/**
 * SPA Virtual Pageview Tracker for Google Tag Manager & Analytics
 * Prevents double firing while ensuring all hash / deep-link transitions are tracked
 */
function trackSpaPageView() {
  try {
    const currentPath = window.location.pathname + (window.location.hash || '');
    if (lastTrackedPath === currentPath) return; // Prevent duplicate firing
    lastTrackedPath = currentPath;

    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'virtual_page_view',
        page_path: currentPath,
        page_title: document.title,
        page_location: window.location.href,
        page_language: getLocale() || 'en'
      });
    }
  } catch (e) {}
}

function navigateTo(route = 'home') {
  const rawRoute = (route || 'home').replace(/^#\/?/, '').replace(/\/+$/, '') || 'home';
  const lang = getLocale() || 'en';

  let cleanRoute = rawRoute;
  let targetArticleSlug = null;

  if (rawRoute.startsWith('news/') || rawRoute.startsWith('latest-news/')) {
    targetArticleSlug = rawRoute.replace(/^(latest-)?news\//, '');
    cleanRoute = 'latest-news';
  } else if (rawRoute.startsWith('case-studies/')) {
    targetArticleSlug = rawRoute.replace(/^case-studies\//, '');
    cleanRoute = 'case-studies';
  } else if (rawRoute.startsWith('product-updates/')) {
    targetArticleSlug = rawRoute.replace(/^product-updates\//, '');
    cleanRoute = 'product-updates';
  } else if (getArticleBySlug(rawRoute, lang)) {
    targetArticleSlug = rawRoute;
    const art = getArticleBySlug(rawRoute, lang);
    const cat = (art.category || 'latest-news').toLowerCase();
    cleanRoute = cat.includes('case') ? 'case-studies' : (cat.includes('product') ? 'product-updates' : 'latest-news');
  }

  updatePageSeoMetadata(rawRoute);
  trackSpaPageView();

  // Sync window hash if not already on the route
  const expectedHash = targetArticleSlug ? `${cleanRoute}/${targetArticleSlug}` : cleanRoute;
  if (window.location.hash.replace(/^#\/?/, '') !== expectedHash) {
    try {
      window.history.replaceState(null, '', `/#${expectedHash}`);
    } catch (e) {}
  }

  // Clear App Container
  app.innerHTML = '';

  // Render Header
  const headerNode = renderHeader(cleanRoute, navigateTo);
  if (headerNode && headerNode instanceof Node) {
    app.appendChild(headerNode);
  }

  // Main View Switcher
  const main = document.createElement('main');
  main.style.flex = '1';

  let pageContentNode;

  if (cleanRoute === 'home') {
    pageContentNode = renderHomePage(navigateTo);
  } else if (cleanRoute === 'solutions') {
    pageContentNode = renderSolutionsPage(navigateTo);
  } else if (cleanRoute.includes('detection')) {
    pageContentNode = renderDetectionPage(cleanRoute, navigateTo);
  } else if (cleanRoute.includes('market')) {
    pageContentNode = renderMarketsPage(cleanRoute, navigateTo);
  } else if (cleanRoute.includes('cooperation')) {
    pageContentNode = renderCooperationPage(cleanRoute, navigateTo);
  } else if (cleanRoute === 'about-us' || cleanRoute === 'about') {
    pageContentNode = renderAboutUsPage(navigateTo);
  } else if (cleanRoute === 'news') {
    pageContentNode = renderNewsHubPage(navigateTo);
  } else if (cleanRoute === 'latest-news') {
    pageContentNode = renderLatestNewsPage(navigateTo);
  } else if (cleanRoute === 'product-updates') {
    pageContentNode = renderProductUpdatesPage(navigateTo);
  } else if (cleanRoute === 'case-studies') {
    pageContentNode = renderCaseStudiesPage(navigateTo);
  } else if (cleanRoute === 'contact') {
    pageContentNode = renderContactPage(navigateTo);
  } else if (cleanRoute === 'cookie-policy') {
    pageContentNode = renderCookiePolicyPage(navigateTo);
  } else if (cleanRoute === 'distributors-map') {
    pageContentNode = renderDistributorsMapPage(navigateTo);
  } else if (cleanRoute === 'privacy-policy') {
    pageContentNode = renderPrivacyPolicyPage(navigateTo);
  } else if (cleanRoute === 'terms-and-conditions' || cleanRoute === 'terms-conditions' || cleanRoute === 'terms') {
    pageContentNode = renderTermsConditionsPage(navigateTo);
  } else if (cleanRoute === 'code-of-ethics') {
    pageContentNode = renderCodeOfEthicsPage(navigateTo);
  } else if (cleanRoute === 'policy-ims') {
    pageContentNode = renderPolicyIMSPage(navigateTo);
  } else if (cleanRoute === 'manuals-and-datasheets' || cleanRoute === 'manuals') {
    pageContentNode = renderManualsAndDatasheetsPage(navigateTo);
  } else {
    pageContentNode = renderHomePage(navigateTo);
  }

  if (pageContentNode && pageContentNode instanceof Node) {
    main.appendChild(pageContentNode);
  }
  app.appendChild(main);

  // Render Footer
  const footerNode = renderFooter(navigateTo);
  if (footerNode && footerNode instanceof Node) {
    app.appendChild(footerNode);
  }

  // Render Global Widgets (Cookie Banner, OpenClaw AI, Article Reader Modal)
  const cookieBannerNode = renderCookieBanner(navigateTo);
  if (cookieBannerNode && cookieBannerNode instanceof Node) {
    app.appendChild(cookieBannerNode);
  }

  // OpenClaw Chat Widget (Persistent across all page navigation)
  renderOpenClawChatWidget();

  const articleModalNode = renderArticleModal();
  if (articleModalNode && articleModalNode instanceof Node) {
    app.appendChild(articleModalNode);
  }

  // If directly accessing an article route, open the modal automatically
  if (targetArticleSlug) {
    setTimeout(() => {
      openArticleModal(targetArticleSlug);
    }, 50);
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

// Router Event Listeners
window.addEventListener('hashchange', () => {
  const currentHash = window.location.hash.replace(/^#\/?/, '');
  navigateTo(currentHash || 'home');
});

// React to Language Changes dynamically across the whole application
window.addEventListener('sed:languageChange', () => {
  const currentHash = window.location.hash.replace(/^#\/?/, '');
  navigateTo(currentHash || 'home');
});

// Initialize Articles and Router on Load
initArticleStore();

let hasInitialized = false;
function initApp() {
  if (hasInitialized) return;
  hasInitialized = true;
  const initialRoute = getInitialRoute();
  navigateTo(initialRoute || 'home');
}

document.addEventListener('DOMContentLoaded', initApp);
if (document.readyState === 'complete' || document.readyState === 'interactive') {
  initApp();
}

// Trigger Automatic IP / Cookie Language Detection for new visitors
initVisitorLanguageDetection();

